import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Lock,
  CheckCheck,
  Download,
  AlertTriangle,
  FileText,
  Pencil,
  Share2,
  Copy,
  ExternalLink,
  MessageSquareText,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = {
  white_collar: "Admin",
  blue_collar: "Blue Collar",
  driver: "Driver",
  manual_entry: "Manual",
};

export default function PayrollRunDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Rev. 2026-02 — Include hr_admin & hr_maker (maker-checker roles).
  const canEdit = ["super_admin", "hr", "hr_admin", "hr_maker"].includes(user?.role);
  const isSuperAdmin = user?.role === "super_admin";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // slip
  const [busy, setBusy] = useState(false);
  const [shareDialog, setShareDialog] = useState(false);
  // Phase 16 — filter / sort / Excel toolbar state
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name");        // name | punch | total | net | advance | gross
  const [sortDir, setSortDir] = useState("asc");       // asc | desc
  const [minTotal, setMinTotal] = useState("");
  const [maxTotal, setMaxTotal] = useState("");
  const [filterPF, setFilterPF] = useState("all");     // all | yes | no
  const [filterESI, setFilterESI] = useState("all");
  const [filterAdvance, setFilterAdvance] = useState("all"); // all | has | none
  const [filterStatus, setFilterStatus] = useState("all"); // all | blocked | excluded

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/payroll/runs/${id}`);
      setData(data);
    } catch (e) {
      toast.error("Failed to load run");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const onApprove = async () => {
    if (!window.confirm("Approve this run? This will lock the run, generate all payslip PDFs, and prevent further edits.")) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(`/payroll/runs/${id}/approve`);
      toast.success(`Approved · ${res.pdfs_generated} PDFs generated`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onDownloadZip = () => {
    window.open(`${API_BASE}/payroll/runs/${id}/zip`, "_blank");
  };

  const onDownloadConsolidated = () => {
    // Phase 5K — bundle EVERY artefact (payslips + registers + bank CSV + summary + README)
    window.open(`${API_BASE}/payroll/runs/${id}/consolidated-zip`, "_blank");
  };

  const [batchSummary, setBatchSummary] = useState(null);
  const loadBatchSummary = async () => {
    try {
      const { data: s } = await api.get(`/payroll/runs/${id}/batch-summary`);
      setBatchSummary(s);
    } catch (e) {
      // silent — surface only if user opens the panel
    }
  };
  useEffect(() => { if (id) loadBatchSummary(); }, [id]);

  const onDownloadTemplate = (variant) => {
    // variant: "zip" | "zip-grid" | "combined"
    const map = {
      "zip": `/payroll/runs/${id}/payslips-zip-template`,
      "zip-grid": `/payroll/runs/${id}/payslips-zip-template-with-grid`,
      "combined": `/payroll/runs/${id}/payslips-combined-pdf`,
    };
    window.open(`${API_BASE}${map[variant]}`, "_blank");
  };

  const onRecompute = async () => {
    if (!window.confirm("Recompute every payslip in this run using the latest formula and attendance summary? HR overrides will be preserved.")) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(`/payroll/runs/${id}/recompute`);
      toast.success(`Recomputed ${res.recomputed} payslips`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onRescanEmployees = async () => {
    if (!window.confirm(
      "Scan the unit for employees with attendance but no payslip in this run?\n\n" +
      "Any employee currently marked 'Working' in this unit who isn't already " +
      "in the payslips list will be added. Existing slips remain untouched — " +
      "you can Recompute afterwards to also refresh existing entries.\n\n" +
      "Proceed?"
    )) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(`/payroll/runs/${id}/rescan-employees`);
      if (res.added === 0) {
        toast.info(`No missing employees. ${res.already_present} already present.`);
      } else {
        const names = (res.employees || []).map(e => `${e.punch_id || "?"} ${e.name}`).join(", ");
        toast.success(`Added ${res.added} employee${res.added === 1 ? "" : "s"}: ${names.slice(0, 200)}${names.length > 200 ? "…" : ""}`);
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onFixAll = async () => {
    if (!window.confirm(
      "🛠 Full Fix — run all 5 fix steps in sequence for this payroll run?\n\n" +
      "  1. Sync driver attendance from /logistics/driver-attendance\n" +
      "  2. Rescan for missing employees (drivers now have summaries)\n" +
      "  3. Clear stale ₹0 locks on drivers who now have valid attendance\n" +
      "  4. Resolve remaining blocked with ₹0 payslip (missing summary)\n" +
      "  5. Recompute the entire run\n\n" +
      "Total time: ~30 seconds. Watch the toast for step-by-step progress.\n\n" +
      "Proceed?",
    )) return;
    setBusy(true);
    try {
      // Step 1 — Sync drivers FIRST so summaries exist before Rescan
      const monthIso = (run?.month || "").slice(0, 10);
      if (monthIso) {
        try {
          const r1 = await api.post(`/driver-attendance/rebuild-summaries?month=${monthIso}`);
          toast.info(`Step 1/5: Drivers — rebuilt ${r1.data.rebuilt || 0}/${r1.data.candidates || 0}`);
        } catch {
          toast.warning("Step 1/5: Sync Drivers skipped (endpoint error)");
        }
      }
      // Step 2 — Rescan (now driver summaries are visible)
      const r2 = await api.post(`/payroll/runs/${id}/rescan-employees`);
      toast.info(`Step 2/5: Rescan — added ${r2.data.added || 0} employee(s)`);
      // Step 3 — Clear stale ₹0 locks
      try {
        const r3 = await api.post(`/payroll/runs/${id}/clear-stale-zero-overrides`);
        if ((r3.data.cleared_count || 0) > 0) {
          toast.info(`Step 3/5: Cleared ${r3.data.cleared_count} stale ₹0 lock(s)`);
        } else {
          toast.info("Step 3/5: No stale ₹0 locks to clear");
        }
      } catch {
        toast.warning("Step 3/5: Clear stale ₹0 locks skipped");
      }
      // Step 4 — Resolve blocked ₹0 (only slips that still have no summary)
      try {
        const r4 = await api.post(`/payroll/runs/${id}/resolve-blocked-zero`);
        toast.info(`Step 4/5: Resolve blocked — ${r4.data.resolved_count || 0} → ₹0 payslip` +
          (r4.data.still_blocked_count > 0 ? `, ${r4.data.still_blocked_count} still blocked (CTC issue)` : ""));
      } catch {
        toast.warning("Step 4/5: Resolve blocked skipped");
      }
      // Step 5 — Recompute
      await api.post(`/payroll/runs/${id}/recompute`);
      toast.success("Step 5/5: Recompute complete — table refreshing");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fix All failed");
    } finally {
      setBusy(false);
    }
  };

  const onSyncDrivers = async () => {
    if (!window.confirm(
      "Sync driver attendance from /logistics/driver-attendance into this " +
      "payroll month?\n\nUse this when drivers show 'No attendance summary' " +
      "or ₹0 salary even though they were marked present on the driver " +
      "attendance page. It force-rebuilds the attendance_monthly_summary " +
      "bridge for every driver, adds any missing driver payslips, clears " +
      "stale ₹0 locks from earlier Fix-All runs, then recomputes.\n\n" +
      "Run's month will be auto-detected.\n\nProceed?",
    )) return;
    setBusy(true);
    try {
      const monthIso = run?.month || run?.period_start || "";
      if (!monthIso) throw new Error("Run month missing");
      const { data: res } = await api.post(
        `/driver-attendance/rebuild-summaries?month=${monthIso.slice(0, 10)}`,
      );
      const collisionMsg = res.skipped_collision_count > 0
        ? ` (${res.skipped_collision_count} skipped — collision with non-driver source)`
        : "";
      toast.success(
        `Rebuilt ${res.rebuilt}/${res.candidates} driver summaries${collisionMsg}. Adding missing…`,
      );
      // Rescan so any missing driver payslips get created now that summaries exist
      const { data: rescanRes } = await api.post(`/payroll/runs/${id}/rescan-employees`);
      if ((rescanRes.added || 0) > 0) {
        toast.info(`Added ${rescanRes.added} driver payslip(s) to run`);
      }
      // Clear stale ₹0 locks on drivers who now have valid attendance
      try {
        const { data: clr } = await api.post(`/payroll/runs/${id}/clear-stale-zero-overrides`);
        if ((clr.cleared_count || 0) > 0) {
          toast.info(`Cleared ${clr.cleared_count} stale ₹0 lock(s)`);
        }
      } catch { /* non-fatal */ }
      // Chain recompute so drivers appear with correct salary
      await api.post(`/payroll/runs/${id}/recompute`);
      toast.success("Recompute complete — refreshing table");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sync failed");
    } finally {
      setBusy(false);
    }
  };

  const onResolveBlockedZero = async () => {
    if (!window.confirm(
      "Auto-resolve blocked employees by generating a ₹0 payslip for anyone " +
      "with a missing attendance summary?\n\n" +
      "Use this when an employee didn't work at all this month (long leave, " +
      "mid-month exit, absconding, etc.). A zero-salary payslip will be created " +
      "so the record is preserved and payroll can be approved.\n\n" +
      "Note: Employees with missing CTC will remain blocked (fix CTC on " +
      "Employee master first).\n\nProceed?"
    )) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(`/payroll/runs/${id}/resolve-blocked-zero`);
      if (res.resolved_count === 0 && res.still_blocked_count === 0) {
        toast.info("No blocked employees found.");
      } else if (res.resolved_count > 0) {
        toast.success(
          `Resolved ${res.resolved_count} employee${res.resolved_count === 1 ? "" : "s"} with ₹0 payslip` +
          (res.still_blocked_count > 0 ? ` — ${res.still_blocked_count} still blocked (CTC issue)` : ""),
        );
      } else {
        toast.warning(`${res.still_blocked_count} employee${res.still_blocked_count === 1 ? "" : "s"} still blocked — fix CTC first`);
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onExcludeSlip = async (slip) => {
    if (!window.confirm(
      `Exclude ${slip.employee_name || "this employee"} from this payroll run?\n\n` +
      "Their payslip will not be generated for this month. Their salary history " +
      "will show a gap for this month. Common uses: resigned mid-month (F&F " +
      "handled separately), absconding cases.\n\nYou can restore later if needed.",
    )) return;
    setBusy(true);
    try {
      await api.post(`/payroll/runs/${id}/payslips/${slip.id}/exclude`);
      toast.success(`Excluded ${slip.employee_name || "employee"}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onRestoreSlip = async (slip) => {
    setBusy(true);
    try {
      await api.post(`/payroll/runs/${id}/payslips/${slip.id}/restore`);
      toast.success(`Restored ${slip.employee_name || "employee"}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onDownloadSlip = (slipId) => {
    window.open(`${API_BASE}/payroll/payslips/${slipId}/pdf`, "_blank");
  };

  const totals = useMemo(() => {
    const t = { gross: 0, net: 0, basic: 0, ot: 0, pf: 0, esi: 0, empCost: 0 };
    if (!data) return t;
    for (const s of data.payslips) {
      t.gross += s.gross_salary;
      t.net += s.net_salary;
      t.basic += s.basic;
      t.ot += s.ot_earning;
      t.pf += s.pf_employee;
      t.esi += s.esi_employee;
      t.empCost += s.total_employer_cost;
    }
    return t;
  }, [data]);

  if (loading || !data) {
    return <div className="text-sm text-slate-500">Loading payroll run...</div>;
  }
  const { run, payslips } = data;
  const isLocked = run.status !== "draft";
  // Excluded slips don't count toward the block. Blocked count only reflects
  // active (non-excluded) slips that still have missing data.
  const blockedCount = payslips.filter((s) => s.blocked && !s.excluded).length;
  const excludedCount = payslips.filter((s) => s.excluded).length;

  // Phase 16 — filter + sort
  const filteredPayslips = (() => {
    let rows = [...payslips];
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((s) =>
        (s.employee_name || "").toLowerCase().includes(q) ||
        (s.punch_id || "").toString().toLowerCase().includes(q),
      );
    }
    if (filterPF !== "all") {
      rows = rows.filter((s) => (filterPF === "yes"
        ? (s.pf_employee || 0) > 0 : (s.pf_employee || 0) === 0));
    }
    if (filterESI !== "all") {
      rows = rows.filter((s) => (filterESI === "yes"
        ? (s.esi_employee || 0) > 0 : (s.esi_employee || 0) === 0));
    }
    if (filterAdvance !== "all") {
      rows = rows.filter((s) => (filterAdvance === "has"
        ? (s.advance || 0) > 0 : (s.advance || 0) === 0));
    }
    if (filterStatus === "blocked") {
      rows = rows.filter((s) => s.blocked && !s.excluded);
    } else if (filterStatus === "excluded") {
      rows = rows.filter((s) => s.excluded);
    }
    const mn = parseFloat(minTotal);
    const mx = parseFloat(maxTotal);
    if (!isNaN(mn)) rows = rows.filter((s) =>
      (s.total_count_override ?? s.total_count ?? 0) >= mn);
    if (!isNaN(mx)) rows = rows.filter((s) =>
      (s.total_count_override ?? s.total_count ?? 0) <= mx);

    const dir = sortDir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      let av, bv;
      switch (sortBy) {
        case "punch":
          av = String(a.punch_id || ""); bv = String(b.punch_id || "");
          // numeric-aware comparison
          return av.localeCompare(bv, undefined, { numeric: true }) * dir;
        case "total":
          av = a.total_count_override ?? a.total_count ?? 0;
          bv = b.total_count_override ?? b.total_count ?? 0; break;
        case "net":   av = a.net_salary || 0;   bv = b.net_salary || 0; break;
        case "gross": av = a.gross_salary || 0; bv = b.gross_salary || 0; break;
        case "advance": av = a.advance || 0; bv = b.advance || 0; break;
        case "name":
        default:
          av = String(a.employee_name || ""); bv = String(b.employee_name || "");
          return av.localeCompare(bv, "en", { sensitivity: "base" }) * dir;
      }
      return (av - bv) * dir;
    });
    return rows;
  })();

  const onDownloadExcel = () => {
    const headers = [
      "Punch", "Name", "Department", "Pay Mode", "CTC", "Monthly Gross",
      "Paid Days", "EL Used", "PL Used", "LOP Days", "LOP Amount",
      "Extra Pay Days",
      "OT Days", "Total Cnt",
      "Salary", "OT", "Incentive+Bonus", "Gross",
      "PF", "ESI", "Advance", "TDS", "Other Ded",
      "Net", "Employer Cost",
    ];
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const lines = [headers.join(",")];
    filteredPayslips.forEach((s) => {
      lines.push([
        s.punch_id, s.employee_name, s.department_name || "",
        s.credit_in || "Bank",
        s.ctc || 0,
        s.gross_monthly || 0,
        s.day_count != null ? Number(s.day_count).toFixed(2) : "",
        Number(s.el_used || 0).toFixed(2),
        Number(s.pl_used || 0).toFixed(2),
        Number(s.lop_days || 0).toFixed(2),
        s.lop_amount || 0,
        Number(s.extra_pay_days || 0).toFixed(2),
        s.ot_count != null ? Number(s.ot_count).toFixed(2) : "",
        (s.total_count_override ?? s.total_count ?? 0).toFixed(2),
        s.salary_base != null ? s.salary_base : (s.salary_calculation || 0),
        s.salary_ot != null ? s.salary_ot : (s.ot_earning || 0),
        (s.incentive || 0) + (s.p_bonus || 0), s.gross_salary || 0,
        s.pf_employee || 0, s.esi_employee || 0,
        s.advance || 0, s.tds || 0, s.other_deductions || 0,
        s.net_salary || 0, s.total_employer_cost || 0,
      ].map(escape).join(","));
    });
    const csv = "\uFEFF" + lines.join("\n");
    const monthLabel = new Date(run.month).toLocaleDateString("en-IN",
      { month: "short", year: "numeric" }).replace(" ", "_");
    const fname = `Payroll_${run.unit_name || "Unit"}_${monthLabel}.csv`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = fname;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${filteredPayslips.length} rows`);
  };

  const onClearFilters = () => {
    setSearch(""); setSortBy("name"); setSortDir("asc");
    setMinTotal(""); setMaxTotal("");
    setFilterPF("all"); setFilterESI("all"); setFilterAdvance("all");
  };

  return (
    <div data-testid="payroll-run-page" className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/payroll" className="text-slate-500 hover:text-slate-900">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Payroll run</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            {run.unit_name} · {new Date(run.month).toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Days divisor: <span className="font-semibold">{run.days_in_month}</span>
            {" · "}
            {run.employee_count} employees
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isLocked ? (
            <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
              <Lock className="h-3 w-3 mr-1" /> {run.status.toUpperCase()}
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200">DRAFT</Badge>
          )}
          {!isLocked && isSuperAdmin && (
            <Button
              data-testid="recompute-btn"
              onClick={onRecompute}
              disabled={busy}
              variant="outline"
            >
              Recompute
            </Button>
          )}
          {!isLocked && (
            <Button
              data-testid="rescan-employees-btn"
              onClick={onRescanEmployees}
              disabled={busy}
              variant="outline"
              title="Add employees who have attendance but no payslip yet (e.g. joined after run was created)"
            >
              + Add missing
            </Button>
          )}
          {!isLocked && isSuperAdmin && blockedCount > 0 && (
            <Button
              data-testid="fix-all-btn"
              onClick={onFixAll}
              disabled={busy}
              className="bg-purple-600 hover:bg-purple-700 text-white font-semibold gap-1.5"
              title="One-click: Rescan → Sync Drivers → Resolve blocked (₹0) → Recompute"
            >
              🛠 Fix All
            </Button>
          )}
          {!isLocked && isSuperAdmin && blockedCount > 0 && (
            <Button
              data-testid="sync-drivers-btn"
              onClick={onSyncDrivers}
              disabled={busy}
              variant="outline"
              className="border-blue-500 text-blue-700 hover:bg-blue-50"
              title="Rebuild summaries for drivers marked on /logistics/driver-attendance page and auto-recompute"
            >
              Sync Drivers
            </Button>
          )}
          {!isLocked && isSuperAdmin && blockedCount > 0 && (
            <Button
              data-testid="resolve-blocked-zero-btn"
              onClick={onResolveBlockedZero}
              disabled={busy}
              variant="outline"
              className="border-amber-500 text-amber-700 hover:bg-amber-50"
              title="Auto-generate ₹0 payslip for employees with missing attendance summary (long leave / mid-month exit)"
            >
              Resolve blocked (₹0)
            </Button>
          )}
          {!isLocked && isSuperAdmin && (
            <Button
              data-testid="approve-btn"
              onClick={onApprove}
              disabled={busy || blockedCount > 0}
              className="brand-bg brand-bg-hover text-white"
            >
              <CheckCheck className="h-4 w-4 mr-2" />
              Approve & generate PDFs
            </Button>
          )}
          {isLocked && (
            <Button data-testid="download-zip-btn" variant="outline" onClick={onDownloadZip}>
              <Download className="h-4 w-4 mr-2" />
              Download ZIP (legacy)
            </Button>
          )}
          {data?.payslips?.length > 0 && (
            <>
              <Button
                data-testid="download-template-zip-btn"
                variant="outline"
                onClick={() => onDownloadTemplate("zip")}
                title="One PDF per employee in legacy template format, all zipped"
              >
                <FileText className="h-4 w-4 mr-2" />
                Payslips ZIP
              </Button>
              <Button
                data-testid="download-template-grid-btn"
                variant="outline"
                onClick={() => onDownloadTemplate("zip-grid")}
                title="Same as Payslips ZIP, with daily attendance grid appended"
              >
                <FileText className="h-4 w-4 mr-2" />
                Payslips + Grid
              </Button>
              <Button
                data-testid="download-template-combined-btn"
                variant="outline"
                onClick={() => onDownloadTemplate("combined")}
                title="Single PDF, 2 payslips per page, only employees with NET > 0"
              >
                <FileText className="h-4 w-4 mr-2" />
                Combined 2-up
              </Button>
              <Button
                data-testid="download-consolidated-btn"
                onClick={onDownloadConsolidated}
                title="Phase 5K — All payslips + Salary Register + PF/ESI/Bank files + Cost Summary + Compliance README in one ZIP"
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                <Download className="h-4 w-4 mr-2" />
                Compliance Bundle ZIP
              </Button>
              {canEdit && (
                <Button
                  data-testid="open-share-dialog"
                  onClick={() => setShareDialog(true)}
                  title="Generate public WhatsApp links to send payslips to blue-collar/driver staff"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Share Payslip Links
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {shareDialog && (
        <ShareLinksDialog
          runId={id}
          onClose={() => setShareDialog(false)}
        />
      )}

      {/* Phase 5J/5K — Stale data detector. If statutory_bonus / gratuity is missing
          on any slip, prompt admin to recompute so new compliance fields populate. */}
      {data?.payslips?.length > 0 && data.payslips.some(s =>
        s.statutory_bonus == null || s.gratuity == null) && !isLocked && (
        <div className="bg-amber-50 border border-amber-300 rounded-md p-3 text-sm text-amber-900 flex items-center gap-2" data-testid="stale-data-banner">
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
          <div className="flex-1">
            <strong>This run uses pre-Phase 5J compute logic.</strong> Statutory Bonus, DA, Gratuity, and updated ESI-on-Basic+DA fields are missing.
            Click <strong>Recompute</strong> above to refresh all {data.payslips.length} slips with the new compliance format.
          </div>
        </div>
      )}

      {blockedCount > 0 && !isLocked && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5" />
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span>
                <b>{blockedCount}</b> employee{blockedCount === 1 ? "" : "s"} blocked due to missing data —
                fix CTC or attendance summary before approving.
              </span>
              <button
                data-testid="show-blocked-btn"
                onClick={() => setFilterStatus(filterStatus === "blocked" ? "all" : "blocked")}
                className={`text-xs px-2 py-0.5 rounded border font-semibold transition ${
                  filterStatus === "blocked"
                    ? "bg-red-600 text-white border-red-600"
                    : "bg-white text-red-700 border-red-300 hover:bg-red-100"
                }`}
              >
                {filterStatus === "blocked" ? "✓ Showing blocked" : "Show blocked only"}
              </button>
            </div>
            <div className="text-xs text-red-700/80 mt-1">
              For employees who didn&apos;t work this month (long leave / mid-month exit): use
              <b className="mx-1">Resolve blocked (₹0)</b>
              above to auto-generate ₹0 payslips, or click ✕ on individual rows to Exclude them from this run.
            </div>
          </div>
        </div>
      )}

      {excludedCount > 0 && !isLocked && (
        <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5 text-xs text-slate-600 flex items-center gap-2 flex-wrap">
          <span className="font-medium">{excludedCount} employee{excludedCount === 1 ? "" : "s"} excluded</span>
          <span className="text-slate-400">— not counted in totals, no payslip PDF will be generated.</span>
          <button
            data-testid="show-excluded-btn"
            onClick={() => setFilterStatus(filterStatus === "excluded" ? "all" : "excluded")}
            className={`text-xs px-2 py-0.5 rounded border font-semibold transition ${
              filterStatus === "excluded"
                ? "bg-slate-700 text-white border-slate-700"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
            }`}
          >
            {filterStatus === "excluded" ? "✓ Showing excluded" : "Show excluded only"}
          </button>
        </div>
      )}

      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card label="Total Gross" value={formatINR(totals.gross)} />
        <Card label="Total Net (paid)" value={formatINR(totals.net)} accent />
        <Card label="OT Earning" value={formatINR(totals.ot)} />
        <Card label="Total Employer Cost" value={formatINR(totals.empCost)} />
      </div>

      {/* Phase 5K — Compliance Snapshot */}
      {batchSummary && batchSummary.deadlines && (
        <div className="bg-gradient-to-br from-amber-50 via-white to-red-50 border border-amber-200 rounded-lg overflow-hidden" data-testid="batch-summary-card">
          <div className="px-4 py-2.5 border-b border-amber-200 flex items-center gap-2 bg-amber-100/40">
            <AlertTriangle className="h-4 w-4 text-amber-700" />
            <div className="text-xs font-bold uppercase tracking-wider text-amber-900">
              Compliance Snapshot — Batch {batchSummary.batch_id}
            </div>
            <div className="ml-auto text-[11px] text-slate-600">
              {batchSummary.successful}/{batchSummary.total_employees} processed
              {batchSummary.failed > 0 && <span className="text-red-600 ml-1">· {batchSummary.failed} failed</span>}
            </div>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] font-semibold uppercase text-slate-500 tracking-wider mb-2">Statutory Liabilities</div>
              <div className="space-y-1.5 text-xs">
                {batchSummary.deadlines.map((d, idx) => (
                  <div key={idx} className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded px-2.5 py-1.5"
                        data-testid={`deadline-${idx}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-800 truncate">{d.task}</div>
                      <div className="text-[10px] text-slate-500">Due: <strong className={d.due.match(/^\d/) ? "text-red-700" : "text-slate-600"}>{d.due}</strong></div>
                    </div>
                    <div className="font-mono font-bold text-slate-900 text-right whitespace-nowrap">
                      {d.amount_due > 0 ? formatINR(d.amount_due) : "—"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase text-slate-500 tracking-wider mb-2">Next Actions</div>
              <ul className="space-y-1 text-xs text-slate-700">
                {batchSummary.next_actions.map((a, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="text-red-600 mt-0.5">→</span>
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 grid grid-cols-2 gap-1.5 text-[11px]">
                <div className="bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5">
                  <div className="text-[9px] uppercase text-emerald-700 font-semibold">Total Bonus</div>
                  <div className="font-mono font-bold text-emerald-900">{formatINR(batchSummary.totals.statutory_bonus)}</div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded px-2 py-1.5">
                  <div className="text-[9px] uppercase text-blue-700 font-semibold">Gratuity Prov.</div>
                  <div className="font-mono font-bold text-blue-900">{formatINR(batchSummary.totals.gratuity_provision)}</div>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded px-2 py-1.5">
                  <div className="text-[9px] uppercase text-purple-700 font-semibold">Total TDS</div>
                  <div className="font-mono font-bold text-purple-900">{formatINR(batchSummary.totals.tds)}</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded px-2 py-1.5">
                  <div className="text-[9px] uppercase text-red-700 font-semibold">Total LOP</div>
                  <div className="font-mono font-bold text-red-900">{formatINR(batchSummary.totals.loss_of_pay)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {/* Phase 16 — Filter + Excel toolbar */}
        <div className="border-b border-slate-100 bg-slate-50/60 px-3 py-2.5
                          flex flex-wrap items-end gap-2"
             data-testid="payroll-filter-bar">
          <div className="flex-1 min-w-[200px]">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 mb-0.5">
              Search
            </div>
            <Input className="h-8 text-xs" value={search}
                   onChange={(e) => setSearch(e.target.value)}
                   placeholder="Name or Punch ID…"
                   data-testid="payroll-search" />
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 mb-0.5">
              Sort
            </div>
            <div className="flex gap-1">
              <select className="h-8 text-xs border border-slate-200 rounded px-1"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      data-testid="payroll-sort-by">
                <option value="name">Name (A–Z)</option>
                <option value="punch">Punch ID (#)</option>
                <option value="total">Total Count</option>
                <option value="gross">Gross</option>
                <option value="net">Net</option>
                <option value="advance">Advance</option>
              </select>
              <button onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")}
                      className="h-8 w-8 border border-slate-200 rounded
                                 hover:bg-slate-100 text-xs font-mono"
                      title={sortDir === "asc" ? "Ascending" : "Descending"}
                      data-testid="payroll-sort-dir">
                {sortDir === "asc" ? "↑" : "↓"}
              </button>
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 mb-0.5">
              Total Cnt
            </div>
            <div className="flex gap-1">
              <Input className="h-8 text-xs w-16" type="number"
                     placeholder="min" value={minTotal}
                     onChange={(e) => setMinTotal(e.target.value)}
                     data-testid="payroll-min-total" />
              <Input className="h-8 text-xs w-16" type="number"
                     placeholder="max" value={maxTotal}
                     onChange={(e) => setMaxTotal(e.target.value)}
                     data-testid="payroll-max-total" />
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 mb-0.5">
              PF
            </div>
            <select className="h-8 text-xs border border-slate-200 rounded px-1"
                    value={filterPF}
                    onChange={(e) => setFilterPF(e.target.value)}
                    data-testid="payroll-filter-pf">
              <option value="all">All</option>
              <option value="yes">PF Applicable</option>
              <option value="no">No PF</option>
            </select>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 mb-0.5">
              ESI
            </div>
            <select className="h-8 text-xs border border-slate-200 rounded px-1"
                    value={filterESI}
                    onChange={(e) => setFilterESI(e.target.value)}
                    data-testid="payroll-filter-esi">
              <option value="all">All</option>
              <option value="yes">ESI Applicable</option>
              <option value="no">No ESI</option>
            </select>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 mb-0.5">
              Advance
            </div>
            <select className="h-8 text-xs border border-slate-200 rounded px-1"
                    value={filterAdvance}
                    onChange={(e) => setFilterAdvance(e.target.value)}
                    data-testid="payroll-filter-advance">
              <option value="all">All</option>
              <option value="has">Has Advance</option>
              <option value="none">No Advance</option>
            </select>
          </div>
          <button onClick={onClearFilters}
                  className="h-8 px-3 text-xs text-slate-500 hover:text-slate-900"
                  data-testid="payroll-clear-filters">
            Clear
          </button>
          <div className="flex-shrink-0 ml-auto flex items-center gap-2">
            <span className="text-[10px] text-slate-500">
              Showing <b>{filteredPayslips.length}</b> of {payslips.length}
            </span>
            <Button size="sm" variant="outline"
                    onClick={onDownloadExcel}
                    data-testid="payroll-export-csv"
                    className="h-8 text-xs">
              <Download className="h-3.5 w-3.5 mr-1" /> Excel
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="payslips-table">
            <thead className="bg-slate-50 border-b">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-3 py-3">Punch</th>
                <th className="px-3 py-3">Name</th>
                <th className="px-3 py-3" data-testid="th-department">Department</th>
                <th className="px-3 py-3">Pay Mode</th>
                <th className="px-3 py-3 text-right">CTC</th>
                <th className="px-3 py-3 text-right" title="Full-month Gross Salary derived from CTC (Basic + HRA + Conv + DA + Bonus). Excludes OT / Incentive / Advances. Shows what the employee would earn for a full-attendance month." data-testid="th-gross-monthly">Monthly Gross</th>
                <th className="px-3 py-3 text-right" title="Present + Sunday off + Paid Holiday">Paid Days</th>
                <th className="px-3 py-3 text-right" title="Earned Leave / Privileged Leave consumed this month" data-testid="th-el-pl">EL / PL</th>
                <th className="px-3 py-3 text-right" title="Loss-of-Pay days and deduction amount" data-testid="th-lop">LOP</th>
                <th className="px-3 py-3 text-right" title="Extra pay days earned by working on Sunday/holiday (Admin/Production Admin)" data-testid="th-extra-days">Extra Days</th>
                <th className="px-3 py-3 text-right" title="Overtime converted to day-equivalents">OT Days</th>
                <th className="px-3 py-3 text-right">Total Cnt</th>
                <th className="px-3 py-3 text-right">Salary</th>
                <th className="px-3 py-3 text-right">OT</th>
                <th className="px-3 py-3 text-right">Inc/Bonus</th>
                <th className="px-3 py-3 text-right">Gross</th>
                <th className="px-3 py-3 text-right">PF/ESI</th>
                <th className="px-3 py-3 text-right" data-testid="th-advance">Advance</th>
                <th className="px-3 py-3 text-right">TDS/Oth</th>
                <th className="px-3 py-3 text-right brand-text">Net</th>
                <th className="px-3 py-3 w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredPayslips.map((s) => (
                <tr key={s.id} className={`hover:bg-slate-50 ${s.blocked ? "bg-red-50" : ""}`}>
                  <td className="px-3 py-2 font-mono text-xs">{s.punch_id}</td>
                  <td className="px-3 py-2 font-medium text-slate-900">
                    {s.employee_name}
                    {s.is_senior_management && <span className="ml-1 text-[9px] uppercase tracking-wider brand-text font-bold">SM</span>}
                    {s.blocked && (
                      <div className="text-[10px] text-red-700 mt-0.5">
                        {s.missing_data.join(", ")}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-700" data-testid={`dept-${s.punch_id}`}>
                    {s.department_name || <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <span
                      data-testid={`pay-mode-${s.punch_id}`}
                      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ring-1 ${
                        (s.credit_in || "Bank") === "Cash"
                          ? "bg-amber-50 text-amber-800 ring-amber-200"
                          : "bg-emerald-50 text-emerald-800 ring-emerald-200"
                      }`}
                    >
                      {s.credit_in || "Bank"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs">{formatINR(s.ctc)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs text-slate-700" data-testid={`gross-monthly-${s.punch_id}`}>
                    {s.gross_monthly ? formatINR(s.gross_monthly) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums" data-testid={`paid-days-${s.punch_id}`}>
                    {s.day_count != null ? Number(s.day_count).toFixed(2) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs" data-testid={`el-pl-${s.punch_id}`}>
                    {((s.el_used || 0) + (s.pl_used || 0)) > 0 ? (
                      <div className="text-emerald-700">
                        <div>{Number(s.el_used || 0).toFixed(1)} <span className="text-slate-400">/</span> {Number(s.pl_used || 0).toFixed(1)}</div>
                        <div className="text-[9px] text-slate-500">EL / PL</div>
                      </div>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs" data-testid={`lop-${s.punch_id}`}>
                    {(s.lop_days || 0) > 0 ? (
                      <div className="text-red-700">
                        <div className="font-semibold">{Number(s.lop_days).toFixed(1)}d</div>
                        <div className="text-[9px]">-{formatINR(s.lop_amount || 0)}</div>
                      </div>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs" data-testid={`extra-days-${s.punch_id}`}>
                    {(s.extra_pay_days || 0) > 0 ? (
                      <div className="text-emerald-700">
                        <div className="font-semibold">+{Number(s.extra_pay_days).toFixed(1)}d</div>
                        <div className="text-[9px] text-slate-500">Sun/Hol</div>
                      </div>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs text-slate-600" data-testid={`ot-days-${s.punch_id}`}>
                    {s.ot_count != null ? Number(s.ot_count).toFixed(2) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold" data-testid={`total-cnt-${s.punch_id}`}>
                    {(s.total_count_override ?? s.total_count).toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatINR(s.salary_base != null ? s.salary_base : s.salary_calculation)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${(s.salary_ot || s.ot_earning || 0) < 0 ? "text-red-700 font-semibold" : ""}`}>{formatINR(s.salary_ot != null ? s.salary_ot : s.ot_earning)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs">
                    {(s.incentive || s.p_bonus) ? (
                      <span>{formatINR((s.incentive || 0) + (s.p_bonus || 0))}</span>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatINR(s.gross_salary)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs text-red-700">
                    -{formatINR((s.pf_employee || 0) + (s.esi_employee || 0))}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs"
                      data-testid={`adv-cell-${s.punch_id}`}>
                    {(s.advance || 0) > 0 ? (
                      <span className="text-amber-700 font-semibold">
                        -{formatINR(s.advance)}
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs text-red-700">
                    {(s.tds || s.other_deductions) ? "-" + formatINR((s.tds || 0) + (s.other_deductions || 0)) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums brand-text font-bold">{formatINR(s.net_salary)}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 items-center">
                      {s.excluded && (
                        <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-500 border-slate-300 mr-1">
                          Excluded
                        </Badge>
                      )}
                      {canEdit && !isLocked && !s.excluded && (
                        <button
                          data-testid={`edit-slip-${s.punch_id}`}
                          onClick={() => setEditing(s)}
                          className="p-1 hover:bg-slate-100 rounded text-slate-400"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {!s.excluded && (
                        <button
                          data-testid={`pdf-${s.punch_id}`}
                          onClick={() => onDownloadSlip(s.id)}
                          className="p-1 hover:bg-slate-100 rounded text-slate-500"
                          title="Download PDF"
                        >
                          <FileText className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {isSuperAdmin && !isLocked && !s.excluded && (
                        <button
                          data-testid={`exclude-slip-${s.punch_id}`}
                          onClick={() => onExcludeSlip(s)}
                          className="p-1 hover:bg-red-50 rounded text-red-400 hover:text-red-700"
                          title="Exclude from this run (e.g. resigned mid-month, F&F handled separately)"
                          disabled={busy}
                        >
                          ✕
                        </button>
                      )}
                      {isSuperAdmin && !isLocked && s.excluded && (
                        <button
                          data-testid={`restore-slip-${s.punch_id}`}
                          onClick={() => onRestoreSlip(s)}
                          className="p-1 hover:bg-emerald-50 rounded text-emerald-600 text-xs px-2"
                          title="Restore into this payroll run"
                          disabled={busy}
                        >
                          Restore
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Chivo" }}>Edit payslip inputs</DialogTitle>
            <DialogDescription>
              {editing?.employee_name} · CTC {formatINR(editing?.ctc)} · {TYPE_LABEL[editing?.category]}
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <EditForm
              slip={editing}
              onSave={async (payload) => {
                setBusy(true);
                try {
                  await api.patch(`/payroll/payslips/${editing.id}`, payload);
                  toast.success("Payslip updated");
                  setEditing(null);
                  load();
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail));
                } finally {
                  setBusy(false);
                }
              }}
              busy={busy}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Card({ label, value, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-xl font-bold mt-1 ${accent ? "brand-text" : "text-slate-900"}`} style={{ fontFamily: "Chivo" }}>
        {value}
      </div>
    </div>
  );
}

function EditForm({ slip, onSave, busy }) {
  const [tc, setTc] = useState(slip.total_count_override ?? slip.total_count);
  const [ot, setOt] = useState(slip.ot_minutes_override ?? slip.ot_minutes_total);
  const [inc, setInc] = useState(slip.incentive ?? 0);
  const [pb, setPb] = useState(slip.p_bonus ?? 0);
  const [adv, setAdv] = useState(slip.advance ?? 0);
  const [tds, setTds] = useState(slip.tds ?? 0);
  const [oth, setOth] = useState(slip.other_deductions ?? 0);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-slate-500">Total Count (paid days)</label>
          <Input data-testid="ef-total" type="number" step="0.01" value={tc} onChange={(e) => setTc(e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-slate-500">OT minutes</label>
          <Input data-testid="ef-ot" type="number" value={ot} onChange={(e) => setOt(e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-slate-500">Incentive</label>
          <Input data-testid="ef-incentive" type="number" value={inc} onChange={(e) => setInc(e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-slate-500">Production Bonus</label>
          <Input type="number" value={pb} onChange={(e) => setPb(e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-slate-500">Advance</label>
          <Input data-testid="ef-advance" type="number" value={adv} onChange={(e) => setAdv(e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-slate-500">TDS</label>
          <Input type="number" value={tds} onChange={(e) => setTds(e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-slate-500">Other deductions</label>
          <Input type="number" value={oth} onChange={(e) => setOth(e.target.value)} />
        </div>
      </div>
      <DialogFooter>
        <Button
          data-testid="ef-save"
          disabled={busy}
          onClick={() =>
            onSave({
              total_count_override: Number(tc),
              ot_minutes_override: Number(ot) || 0,
              incentive: Number(inc) || 0,
              p_bonus: Number(pb) || 0,
              advance: Number(adv) || 0,
              tds: Number(tds) || 0,
              other_deductions: Number(oth) || 0,
            })
          }
          className="brand-bg brand-bg-hover text-white"
        >
          Save & recompute
        </Button>
      </DialogFooter>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ShareLinksDialog({ runId, onClose }) {
  const [rows, setRows] = useState(null);
  const [config, setConfig] = useState({ max_age_days: 30, one_time_view: true });
  const [generating, setGenerating] = useState(false);
  const [q, setQ] = useState("");

  const loadExisting = async () => {
    try {
      const [cfg, links] = await Promise.all([
        api.get("/payroll/share-config"),
        api.get(`/payroll/runs/${runId}/share-links`),
      ]);
      setConfig(cfg.data);
      setRows(links.data.rows || []);
    } catch (e) {
      toast.error("Failed to load existing links");
      setRows([]);
    }
  };
  useEffect(() => { loadExisting(); /* eslint-disable-next-line */ }, [runId]);

  const generateBulk = async () => {
    if (rows && rows.length > 0) {
      if (!window.confirm("Existing links will be REVOKED and replaced with new ones. Continue?")) return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post(`/payroll/runs/${runId}/share-bulk`, {});
      toast.success(`Generated ${data.count} share links`);
      // Refresh
      await loadExisting();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Generation failed");
    } finally { setGenerating(false); }
  };

  const buildPublicUrl = (token) => {
    const origin = window.location.origin;
    return `${origin}/p/payslip/${token}`;
  };

  const buildWaLink = (phone, msg) => {
    const digits = String(phone || "").replace(/\D/g, "");
    let e164 = digits;
    if (digits.length === 10) e164 = `91${digits}`;
    else if (digits.length === 11 && digits.startsWith("0")) e164 = `91${digits.slice(1)}`;
    return `https://wa.me/${e164}?text=${encodeURIComponent(msg)}`;
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const filtered = (rows || []).filter(r => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (r.employee_name || "").toLowerCase().includes(s) ||
            (r.employee_code || "").toLowerCase().includes(s);
  });

  const withPhone = (rows || []).filter(r => r.phone).length;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-emerald-600" />
            Share Payslip Links — Public WhatsApp / SMS
          </DialogTitle>
          <DialogDescription>
            Generate one-time tokenized links per employee. Send via WhatsApp to staff who don't use the portal.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-4 gap-2 text-center mt-2">
          <div className="bg-slate-50 border rounded p-2"><div className="text-lg font-bold">{rows?.length || 0}</div><div className="text-[10px] uppercase text-slate-500">Total Links</div></div>
          <div className="bg-emerald-50 border border-emerald-200 rounded p-2"><div className="text-lg font-bold text-emerald-700">{withPhone}</div><div className="text-[10px] uppercase text-emerald-700">With Phone</div></div>
          <div className="bg-amber-50 border border-amber-200 rounded p-2"><div className="text-lg font-bold text-amber-700">{(rows?.length || 0) - withPhone}</div><div className="text-[10px] uppercase text-amber-700">No Phone</div></div>
          <div className="bg-red-50 border border-red-200 rounded p-2">
            <div className="text-lg font-bold text-red-700">{config.max_age_days}d</div>
            <div className="text-[10px] uppercase text-red-700">Expiry · {config.one_time_view ? "One-time" : "Multi-use"}</div>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or code…" className="flex-1" data-testid="share-search" />
          <Button onClick={generateBulk} disabled={generating} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="generate-bulk-share">
            {generating ? <><RefreshCw className="h-4 w-4 mr-1 animate-spin" />Generating…</> : <><Share2 className="h-4 w-4 mr-1" />{(rows?.length || 0) > 0 ? "Regenerate All" : "Generate All Links"}</>}
          </Button>
        </div>

        <div className="overflow-y-auto flex-1 border rounded mt-2">
          {rows === null && <p className="text-center text-xs text-slate-400 p-6">Loading…</p>}
          {rows !== null && rows.length === 0 && (
            <div className="text-center p-12">
              <Share2 className="h-10 w-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No share links yet</p>
              <p className="text-xs text-slate-400 mt-1">Click <strong>Generate All Links</strong> above to create tokens for all {rows?.length || "all"} employees in this run.</p>
            </div>
          )}
          {rows !== null && rows.length > 0 && (
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1.5 font-semibold">Employee</th>
                  <th className="text-left px-2 py-1.5 font-semibold">Phone</th>
                  <th className="text-left px-2 py-1.5 font-semibold">Status</th>
                  <th className="text-right px-2 py-1.5 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(r => {
                  const url = buildPublicUrl(r.token);
                  const msg = `Hi ${r.employee_name}, your salary slip is ready. View / Download here: ${url}`;
                  const waUrl = r.phone ? buildWaLink(r.phone, msg) : null;
                  const isActive = r.status === "active";
                  return (
                    <tr key={r.id} data-testid={`share-row-${r.employee_id}`} className={isActive ? "" : "opacity-50"}>
                      <td className="px-2 py-1.5">
                        <div className="font-medium">{r.employee_name}</div>
                        <div className="text-[10px] text-slate-400">{r.employee_code}</div>
                      </td>
                      <td className="px-2 py-1.5 font-mono text-[11px]">{r.phone || <span className="text-amber-600">—</span>}</td>
                      <td className="px-2 py-1.5">
                        <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                          r.status === "active" ? "bg-emerald-100 text-emerald-700" :
                          r.status === "used" ? "bg-blue-100 text-blue-700" :
                          r.status === "expired" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"
                        }`}>{r.status}</span>
                        {r.view_count > 0 && <span className="ml-1 text-[10px] text-slate-400">· {r.view_count} view{r.view_count > 1 ? "s" : ""}</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline" onClick={() => copy(url)} className="h-6 text-[10px]" title="Copy link"><Copy className="h-3 w-3" /></Button>
                          <Button size="sm" variant="outline" onClick={() => window.open(url, "_blank")} className="h-6 text-[10px]" title="Preview"><ExternalLink className="h-3 w-3" /></Button>
                          {waUrl && (
                            <Button size="sm" onClick={() => window.open(waUrl, "_blank")} className="h-6 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`wa-share-${r.employee_id}`} title="Send via WhatsApp">
                              <MessageSquareText className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

